import { useCallback, useEffect, useState } from 'react';

import { UnstyledButton } from '@any-ui-react/core';
import { Combobox, useCombobox } from '@any-ui-react/core';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import classNames from 'classnames';
import {
  $getSelection,
  $isNodeSelection,
  KEY_ARROW_LEFT_COMMAND,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
} from 'lexical';
import { COMMAND_PRIORITY_LOW } from 'lexical';

import { VariableIcon } from '@anyx/shared-icons';


import { useVariablePluginCtx } from '../context';
import { variableBlockBaseClass } from '../hooks';
import { $isVariableNode } from '../VariableNode';
import { INSERT_VARIABLE, UPDATE_VARIABLE } from '../VariablePlugin';

interface VariableDropdownProps {
  mode?: 'toolbar' | 'select';
  variable?: string;
  nodeId?: string;
}

export const VariableDropdown = ({ variable, mode = 'select', nodeId }: VariableDropdownProps) => {
  if (mode === 'select' && !nodeId) {
    throw new Error('nodeId is required when mode is "select"');
  }

  const { options } = useVariablePluginCtx();
  const [currentVariable, setCurrentVariable] = useState(variable);
  const combobox = useCombobox();
  const [editor] = useLexicalComposerContext();
  const [isSelected] = useLexicalNodeSelection(nodeId || '');

  const Target =
    mode === 'toolbar' ? (
      <UnstyledButton
        className={classNames('flex items-center justify-center gap-px', {
          'bg-gray-2': combobox.dropdownOpened,
        })}
        onClick={() => {
          combobox.toggleDropdown();
        }}
      >
        <VariableIcon />
        <Combobox.Chevron />
      </UnstyledButton>
    ) : (
      <UnstyledButton
        className={classNames(variableBlockBaseClass, 'variable-node', {
          'border-blue-6 bg-[#EBF4FC]': combobox.dropdownOpened,
          'border-gray-3': !combobox.dropdownOpened,
          'bg-gray-0': isSelected,
        })}
        onClick={() => {
          combobox.toggleDropdown();
        }}
      >
        {currentVariable}
      </UnstyledButton>
    );

  const $onDelete = useCallback(
    (payload: KeyboardEvent) => {
      const deleteSelection = $getSelection();
      if ($isNodeSelection(deleteSelection)) {
        const event: KeyboardEvent = payload;
        event.preventDefault();
        editor.update(() => {
          deleteSelection.getNodes().forEach((node) => {
            if ($isVariableNode(node)) {
              node.remove();
            }
          });
        });
      }
      return false;
    },
    [editor]
  );

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand<KeyboardEvent>(
        KEY_ARROW_LEFT_COMMAND,
        (event) => {
          const selection = $getSelection();
          if ($isNodeSelection(selection)) {
            event.preventDefault();
            editor.update(() => {
              const node = selection.getNodes()[0];
              if ($isVariableNode(node)) {
                node.selectPrevious();
              }
            });
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand<KeyboardEvent>(
        KEY_ARROW_RIGHT_COMMAND,
        (event) => {
          const selection = $getSelection();
          if ($isNodeSelection(selection)) {
            event.preventDefault();
            editor.update(() => {
              const node = selection.getNodes()[0];
              if ($isVariableNode(node)) {
                node.selectNext();
              }
            });
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(KEY_DELETE_COMMAND, $onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, $onDelete, COMMAND_PRIORITY_LOW)
    );
  }, [$onDelete, editor]);

  return (
    <Combobox
      store={combobox}
      width="fit-content"
      position="bottom-start"
      onOptionSubmit={(value) => {
        setCurrentVariable(value);
        if (mode === 'toolbar') {
          editor.dispatchCommand(INSERT_VARIABLE, { variable: value });
        } else {
          editor.dispatchCommand(UPDATE_VARIABLE, { variable: value, nodeId: nodeId || '' });
        }
        editor.focus();
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>{Target}</Combobox.Target>

      <Combobox.Dropdown className="border-none bg-transparent p-0">
        <Combobox.Options className="flex w-fit flex-col gap-x-1 border bg-white p-1">
          {options.map(({ value, label }) => (
            <Combobox.Option value={value} key={value}>
              {label}
            </Combobox.Option>
          ))}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};
